import { Component, OnInit } from '@angular/core'
import { UntypedFormGroup } from '@angular/forms'
import { Router } from '@angular/router'
import { TranslateService } from '@ngx-translate/core'
import { browserSupportsWebAuthn } from '@simplewebauthn/browser'
import { AlertService } from '../../../providers/service/alert.service'
import { GlobalVariablesService, HunaParseErrorCode } from '../../../providers/service/global-variables.service'
import { UserService } from '../../../providers/service/user.service'
import { InitializeGlobalVariableService } from 'src/app/providers/service/initialize-global-variable.service'
import { TwoFaService } from 'src/app/providers/service/two-fa.service'

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: false
})
export class LoginComponent implements OnInit {
    public loginForm: UntypedFormGroup
    public loading = false
    public hide = true
    public showOtpField = false
    public passkeySupported = browserSupportsWebAuthn()
    private tempCredentials: { username: string; password: string } | null = null

    constructor(
        private userService: UserService,
        private router: Router,
        private alertService: AlertService,
        private translate: TranslateService,
        private globalVariablesService: GlobalVariablesService,
        private initializeGlobalVariableService: InitializeGlobalVariableService,
        private twoFaService: TwoFaService
    ) {
        this.loginForm = this.userService.getFormLogin()
    }

    get logo() {
        return this.globalVariablesService.defaultLogoPc
    }

    async login() {
        this.loading = true
        const { email: username, password, otp } = this.loginForm.value

        try {
            await this.userService.web_loginUser({
                username,
                password,
                otp,
            })

            if (this.showOtpField && this.passkeySupported) {
                await this.proposePasskeyRegistration()
            }
            this.handleSuccessfulLogin()
        } catch (error: any) {
            this.handleLoginError(error, username, password)
        } finally {
            this.loading = false
        }
    }

    async loginWithPasskey() {
        this.loading = true
        const emailControl = this.loginForm.get('email')
        const username = emailControl?.valid ? emailControl.value : undefined

        try {
            await this.userService.web_loginWithPasskey(username)
            this.handleSuccessfulLogin()
        } catch (error: any) {
            this.handlePasskeyError(error)
        } finally {
            this.loading = false
        }
    }

    private async proposePasskeyRegistration() {
        const confirm = await this.twoFaService.showConfirmationDialog(
            'passkey.register.title',
            'passkey.register.message'
        )
        if (!confirm) return

        try {
            await this.userService.web_registerPasskey()
            this.alertService.success(this.translate.instant('passkey.register.success'))
        } catch (error: any) {
            this.handlePasskeyError(error)
        }
    }

    private handlePasskeyError(error: any) {
        // Errors raised by the browser/authenticator never reach the backend
        if (error?.name === 'NotAllowedError' || error?.name === 'AbortError') {
            return
        }
        if (
            error?.name === 'InvalidStateError' ||
            error?.code === HunaParseErrorCode.PASSKEY_ALREADY_REGISTERED
        ) {
            this.alertService.success(this.translate.instant('passkey.error.already_registered'))
            return
        }
        switch (error?.code) {
            case HunaParseErrorCode.PASSKEY_NOT_CONFIGURED:
                this.passkeySupported = false
                this.alertService.error(this.translate.instant('passkey.error.not_configured'))
                break
            case HunaParseErrorCode.PASSKEY_CHALLENGE_INVALID:
            case HunaParseErrorCode.PASSKEY_VERIFICATION_FAILED:
                this.alertService.error(this.translate.instant('passkey.error.verification_failed'))
                break
            case HunaParseErrorCode.PASSKEY_CREDENTIAL_NOT_FOUND:
                this.alertService.error(this.translate.instant('passkey.error.credential_not_found'))
                break
            case HunaParseErrorCode.USER_NOT_AUTHORIZED:
                this.alertService.error(this.translate.instant('passkey.error.user_blocked'))
                break
            default:
                this.alertService.error(error?.message || this.translate.instant('loginFailed'))
        }
    }

    private handleSuccessfulLogin() {
        this.loginForm.reset()
        this.initializeGlobalVariableService.initializeGlobalVariable();
        this.alertService.success(
            this.translate.instant('welcome', { name: this.userService.user?.get('name') })
        )

        this.router.navigate(['/project'])
    }

    private async handleLoginError(error: any, username: string, password: string) {
        
        if (error.message === UserService.MISSING_OTP_ERROR) {
            this.showOtpField = true
            this.tempCredentials = { username, password }
            await this.requestOtp()
        } else {
            this.alertService.error(error.message || this.translate.instant('loginFailed'))
        }
    }

    async requestOtp() {
        if (!this.tempCredentials) return
        this.loading = true
        try {
            const { username, password } = this.tempCredentials
            await this.userService.web_sendOTPToMail({ username, password })
        } catch (error: any) {
            this.alertService.error(error.message)
        } finally {
            this.loading = false
        }
    }

    ngOnInit(): void {
    }

}
